import type { PipelineRun } from "@/hooks/useProjectPipelines";

/** Mirrors server StoryCostSummaryResponse (SD-3). */
export interface StoryCostSummary {
  story_id: string | null;
  work_item_ref: string;
  total_cost_usd: number;
  boundary_source: string | null;
  date: string | null;
  completed_at: string;
  cost_breakdown: {
    by_skill: Array<{ skill: string; category: string | null; cost_usd: number }>;
    by_phase: Array<{ phase: string; cost_usd: number }>;
    by_model: Array<{ model: string; cost_usd: number }>;
  };
}

/**
 * Maximum number of story refs to fan-out for cost queries (D10).
 * Bounds the burst of parallel requests on tab open.
 */
export const MAX_STORY_COSTS = 20;

export interface SkillCostAggregate {
  skill: string;
  cost_usd: number;
}

/**
 * Extract deduplicated story refs from pipeline runs' issue_id field (D10/AR-3).
 * Keeps the most recent started_at per ref, sorts desc, caps at MAX_STORY_COSTS.
 * Null/empty issue_ids are skipped (ad-hoc runs).
 */
export function extractStoryRefs(runs: PipelineRun[]): {
  refs: string[];
  totalRefs: number;
} {
  // Map each ref to its most recent started_at
  const refMap = new Map<string, string | null>();

  for (const run of runs) {
    if (!run.issue_id) continue;
    const existing = refMap.get(run.issue_id);
    if (existing === undefined) {
      refMap.set(run.issue_id, run.started_at);
    } else {
      // Keep the most recent started_at (null sorts last)
      if (
        run.started_at !== null &&
        (existing === null || run.started_at > existing)
      ) {
        refMap.set(run.issue_id, run.started_at);
      }
    }
  }

  const entries = [...refMap.entries()];
  entries.sort((a, b) => {
    const aTime = a[1];
    const bTime = b[1];
    if (aTime === null && bTime === null) return 0;
    if (aTime === null) return 1;
    if (bTime === null) return -1;
    return bTime.localeCompare(aTime); // desc
  });

  const totalRefs = entries.length;
  const refs = entries.slice(0, MAX_STORY_COSTS).map(([ref]) => ref);

  return { refs, totalRefs };
}

/**
 * Aggregate cost by skill across multiple story cost summaries (D12).
 * Null entries are filtered out. Result sorted by cost desc.
 */
export function aggregateBySkill(
  results: Array<StoryCostSummary | null>,
): SkillCostAggregate[] {
  const skillMap = new Map<string, number>();

  for (const result of results) {
    if (result === null) continue;
    for (const entry of result.cost_breakdown.by_skill) {
      const existing = skillMap.get(entry.skill) ?? 0;
      skillMap.set(entry.skill, existing + entry.cost_usd);
    }
  }

  return [...skillMap.entries()]
    .map(([skill, cost_usd]) => ({ skill, cost_usd }))
    .sort((a, b) => b.cost_usd - a.cost_usd);
}
